import get from 'lodash/get';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { RESOURCE_TYPE_PROJECT_CONTEXT } from '@atlassian/jira-router-resources-project-context/src/constants.tsx';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { consumeDependency } from '@atlassian/jira-router-resources-utils/src/utils/consume-dependency/index.tsx';
import { getProjectKeyId } from '@atlassian/jira-router-resources-utils/src/utils/get-project-key-id/index.tsx';
import type { IssueListResponse } from '@atlassian/jira-servicedesk-queues-common/src/rest/issue/types.tsx';
import {
	createResource,
	type RouterContext,
	type CreateResourceArgSync,
} from '@atlassian/react-resource-router';
import { getUserProperty } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import {
	ONE_DAY_IN_MILLIS,
	RESOURCE_TYPE_SERVICEDESK_QUEUES_NAVIGATION_ITEMS,
	RESOURCE_TYPE_SERVICEDESK_QUEUES_PRIORITY_GROUPS,
	RESOURCE_TYPE_SERVICEDESK_QUEUES_ISSUE_LIST_SSR,
	SERVICEDESK_QUEUES_SIDEBAR_VIEW_ALL_QUEUES,
	SERVICEDESK_QUEUES_NAV_4_SIDEBAR_SELECTION,
	SERVICEDESK_QUEUES_SIDEBAR_QUEUES_CATEGORY,
	LOCAL_STORAGE_KEY_NAV_LOCATION,
	LOCAL_STORAGE_KEY_CATEGORY,
} from '../constants.tsx';
import type { CategorizedGroupData, QueueNavState, QueuesCategoryState } from '../types.tsx';
import { getPriorityGroupDataByProjectId } from './get-priority-group-data-by-project-id/index.tsx';

const getDataLoader = () =>
	import(
		/* webpackChunkName: "async-resource-servicedesk-queues-nav-items", jiraSpaEntry: "async-resource-servicedesk-queues-nav-items" */ './get-data'
	);

const getPriorityGroupDataLoader = () =>
	import(
		/* webpackChunkName: "async-resource-servicedesk-queues-priority-group-data-loader", jiraSpaEntry: "async-resource-servicedesk-queues-priority-group-data-loader" */ './get-priority-group-data'
	);

const fetchIssueListDataLoader = () =>
	import(
		/* webpackChunkName: "async-resource-servicedesk-queues-issue-list-data-loader", jiraSpaEntry: "async-resource-servicedesk-queues-issue-list-data-loader" */ './fetch-issue-list-ssr-data'
	);

const getCombinedProjectKeyAndQueueId = ({ match, query }: RouterContext) =>
	`${getProjectKeyId(match, query).projectKey || get(match, 'params.issueKey', '').split('-')[0]}-${get(match, 'params.queueId', '')}`;

const getResourceKeyFromProjectKey = ({ match, query }: Pick<RouterContext, 'match' | 'query'>) =>
	`${getProjectKeyId(match, query).projectKey || get(match, 'params.issueKey', '').split('-')[0]}`;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const queuesNavigationItems = createResource<any>({
	type: RESOURCE_TYPE_SERVICEDESK_QUEUES_NAVIGATION_ITEMS,
	getKey: getResourceKeyFromProjectKey,
	getDataLoader,
	maxAge: ONE_DAY_IN_MILLIS,
});

export const priorityGroupResource = createResource<CategorizedGroupData>({
	type: RESOURCE_TYPE_SERVICEDESK_QUEUES_PRIORITY_GROUPS,
	getKey: getResourceKeyFromProjectKey,
	getDataLoader: getPriorityGroupDataLoader,
	maxAge: ONE_DAY_IN_MILLIS,
	/**
	 * This resource will not load in SSR. Because priority group data resides in the JSM Kukama microservice.
	 * If we wanna fetch this resource during SSR, the request we're making from Tesseract -> Kukama is S2S, where Tesseract needs to know cookies information.
	 * We initially skipped SSR for fetching this resource, because we couldn’t retrieve the request cookie from JFE.
	 * Iiuc, the Jira team doesnt doesnt support this due to security concerns (the info is from JSM Queues team).
	 */
	isBrowserOnly: true,
});

const priorityGroupCrossProjectResourcePayload: CreateResourceArgSync<CategorizedGroupData> = {
	type: RESOURCE_TYPE_SERVICEDESK_QUEUES_PRIORITY_GROUPS,
	getKey: getResourceKeyFromProjectKey,
	getData: async ({ match, query }, { tenantContext: { cloudId } }) => {
		const projectKey = getProjectKeyId(match, query).projectKey;
		try {
			const projectId = match.params.projectId ? +match.params.projectId : undefined;
			return getPriorityGroupDataByProjectId(projectId, projectKey, cloudId);
		} catch (error) {
			fireErrorAnalytics({
				meta: {
					id: 'priorityGroupCrossProjectNoProjectId',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			return getPriorityGroupDataByProjectId(undefined, projectKey, cloudId);
		}
	},
	maxAge: ONE_DAY_IN_MILLIS,
	depends: [],
};

const priorityGroupByProjectIdResourcePayload: CreateResourceArgSync<CategorizedGroupData> = {
	type: RESOURCE_TYPE_SERVICEDESK_QUEUES_PRIORITY_GROUPS,
	getKey: getResourceKeyFromProjectKey,
	getData: async ({ match, query, dependencies }, { tenantContext: { cloudId } }) => {
		fireErrorAnalytics({
			meta: {
				id: 'priorityGroupByProjectIdStart',
				packageName: 'jiraRouterResourcesServiceDeskQueues',
				teamName: 'jsm-flash',
			},
			sendToPrivacyUnsafeSplunk: true,
		});

		const { projectKey } = getProjectKeyId(match, query);

		if (!projectKey) {
			fireErrorAnalytics({
				meta: {
					id: 'priorityGroupByProjectIdNoProjectKey',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		if (!cloudId) {
			fireErrorAnalytics({
				meta: {
					id: 'priorityGroupByProjectIdNoCloudId',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		let data;
		try {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			data = (await consumeDependency(
				dependencies,
				RESOURCE_TYPE_PROJECT_CONTEXT,
			)) as ProjectContext;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'priorityGroupByProjectIdProjectContextError',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		if (!data) {
			fireErrorAnalytics({
				meta: {
					id: 'priorityGroupByProjectIdNoProjectContextData',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		const projectId = data?.projectId;

		if (!projectId) {
			fireErrorAnalytics({
				meta: {
					id: 'priorityGroupByProjectIdNoProjectContextProjectId',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		try {
			return getPriorityGroupDataByProjectId(projectId, projectKey, cloudId);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'priorityGroupByProjectIdGettingPriorityGroupsDataError',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});

			return getPriorityGroupDataByProjectId(undefined, projectKey, cloudId);
		}
	},
	maxAge: ONE_DAY_IN_MILLIS,
	depends: [RESOURCE_TYPE_PROJECT_CONTEXT],
};

export const priorityGroupByProjectIdResourceSsr = createResource<CategorizedGroupData>(
	priorityGroupByProjectIdResourcePayload,
);

export const priorityGroupCrossProjectResource = createResource<CategorizedGroupData>({
	...priorityGroupCrossProjectResourcePayload,
	isBrowserOnly: true,
});

export const issueListSsrResource = createResource<IssueListResponse | undefined>({
	type: RESOURCE_TYPE_SERVICEDESK_QUEUES_ISSUE_LIST_SSR,
	getKey: getCombinedProjectKeyAndQueueId,
	getDataLoader: fetchIssueListDataLoader,
	maxAge: 0,
	// run this resource from server only
	skipBrowser: true,
});

export const queueSidebarViewAllQueuesResource = createResource<boolean | undefined>({
	type: SERVICEDESK_QUEUES_SIDEBAR_VIEW_ALL_QUEUES,
	getKey: getResourceKeyFromProjectKey,
	getData: async ({ match, query }, { tenantContext: { atlassianAccountId } }) => {
		const projectKey = getResourceKeyFromProjectKey({ match, query });

		try {
			if (!atlassianAccountId) {
				throw new Error('accountId is missing.');
			}
			const response = await getUserProperty<boolean>(
				atlassianAccountId,
				`jsm-all-queues-state_is-open-${projectKey}`,
			);
			return response;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'fetchJsmQueueSidebarViewAllQueuesResourceError',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	},
	maxAge: ONE_DAY_IN_MILLIS,
});

export const queueNav4SidebarSelectionResource = createResource<QueueNavState | undefined>({
	type: SERVICEDESK_QUEUES_NAV_4_SIDEBAR_SELECTION,
	getKey: getResourceKeyFromProjectKey,
	getData: async (_, { tenantContext: { atlassianAccountId } }) => {
		try {
			if (!atlassianAccountId) {
				throw new Error('accountId is missing.');
			}
			const response = await getUserProperty<QueueNavState>(
				atlassianAccountId,
				LOCAL_STORAGE_KEY_NAV_LOCATION,
			);
			return response;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'fetchQueueNav4SidebarSelectionResourceError',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	},
	maxAge: ONE_DAY_IN_MILLIS,
});

export const queueSidebarQueuesCategoryStateResource = createResource<
	QueuesCategoryState | undefined
>({
	type: SERVICEDESK_QUEUES_SIDEBAR_QUEUES_CATEGORY,
	getKey: getResourceKeyFromProjectKey,
	getData: async (_, { tenantContext: { atlassianAccountId } }) => {
		try {
			if (!atlassianAccountId) {
				throw new Error('accountId is missing.');
			}
			const response = await getUserProperty<QueuesCategoryState>(
				atlassianAccountId,
				LOCAL_STORAGE_KEY_CATEGORY,
			);
			return response;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'fetchJsmQueueSidebarQueuesCategoryStateResourceError',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	},
	maxAge: ONE_DAY_IN_MILLIS,
});
